'use client'

import {
    UserCommissionMode,
    getUserCommission,
    removeUserCommission,
    upsertUserCommission,
} from '@/lib/api/users/users-api'
import { currencyFormat, parseCurrencyToCents } from '@/lib/form-state/money'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { useEffect, useState } from 'react'
import { MdInfoOutline, MdPaid } from 'react-icons/md'
import { toast } from 'react-toastify'

interface UserCommissionSectionProps {
    /** Usuário em edição. A seção não existe na criação — não há id ainda. */
    userId: number
    userName: string
}

/** Percentual digitado ("12", "12,5") em basis points. 0 quando não há número válido. */
function parsePercentToBasisPoints(input: string): number {
    const normalized = (input || '').replace(',', '.').trim()
    const value = Number(normalized)
    if (!normalized || Number.isNaN(value) || value <= 0) return 0
    return Math.round(value * 100)
}

/** Basis points de volta para o campo ("1250" -> "12,5"). */
function formatBasisPoints(basisPoints: number | null): string {
    if (basisPoints == null) return ''
    return (basisPoints / 100).toLocaleString('pt-BR')
}

/** Centavos de volta para o campo mascarado ("500" -> "R$ 5,00"). */
function formatCents(cents: number | null): string {
    if (cents == null) return ''
    return currencyFormat(String(cents), true)
}

type RateKey = 'rate' | 'rateConsultation' | 'rateExam' | 'rateOrder'
type TypeKey = 'Consultation' | 'Exam' | 'Order'

/**
 * Critério escolhido para um tipo. `''` = segue o geral, que é o padrão e o caso
 * comum ("tudo percentual, exame paga menos").
 */
type TypeMode = '' | UserCommissionMode

/**
 * Comissão por produtividade do usuário.
 *
 * **É aqui que a taxa passa a viver.** Ela era digitada no modal de comissão, a cada
 * apuração: nada ficava registrado sobre o que havia sido combinado, e duas pessoas
 * apurando o mesmo mês chegavam a valores diferentes sem que nada denunciasse. A aba
 * "Produtividade" agora só lê — não há mais campo de taxa lá.
 *
 * **Mora no usuário, e não na ficha de RH**, porque a apuração é por usuário do sistema
 * (é ele que cria o agendamento) e nem todo mundo que agenda tem ficha: em dev, 9
 * funcionários para 81 usuários, e o cadastro de RH não tem contrato de PJ nem de
 * autônomo.
 *
 * **Editar exige permissão de RH ou de financeiro**, não a de administrar usuários:
 * quem cuida de acesso (TI, secretaria) mexe no login, mas não na remuneração. O
 * backend aplica a mesma regra — aqui é só a tela.
 */
export default function UserCommissionSection({
    userId,
    userName,
}: UserCommissionSectionProps) {
    const labels = useServiceLabel()
    const hr = useResourcePermissions('hr', 'employees')
    const financial = useResourcePermissions('financial', 'billings')
    const canEdit = hr.canUpdate || financial.canUpdate

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [enabled, setEnabled] = useState(false)
    const [mode, setMode] = useState<UserCommissionMode>('PERCENTAGE')
    const [rates, setRates] = useState<Record<RateKey, string>>({
        rate: '',
        rateConsultation: '',
        rateExam: '',
        rateOrder: '',
    })
    /** Critério próprio de cada tipo. Vazio = herda o geral. */
    const [typeModes, setTypeModes] = useState<Record<TypeKey, TypeMode>>({
        Consultation: '',
        Exam: '',
        Order: '',
    })
    const [updatedBy, setUpdatedBy] = useState<string | null>(null)
    const [updatedAt, setUpdatedAt] = useState<string | null>(null)

    const isPercentage = mode === 'PERCENTAGE'

    useEffect(() => {
        let active = true
        setLoading(true)
        getUserCommission(userId)
            .then(res => {
                if (!active) return
                const commission = res.success ? res.result : null
                if (!commission) {
                    setEnabled(false)
                    return
                }
                // Cada taxa é formatada pelo critério que vale para ELA: num acordo
                // misto, o campo de exame é percentual enquanto o de consulta é moeda.
                const toField = (
                    value: number | null,
                    valueMode: UserCommissionMode,
                ) =>
                    valueMode === 'PERCENTAGE'
                        ? formatBasisPoints(value)
                        : formatCents(value)
                setEnabled(true)
                setMode(commission.mode)
                setTypeModes({
                    Consultation: commission.modeConsultation ?? '',
                    Exam: commission.modeExam ?? '',
                    Order: commission.modeOrder ?? '',
                })
                setRates({
                    rate: toField(commission.rate, commission.mode),
                    rateConsultation: toField(
                        commission.rateConsultation,
                        commission.modeConsultation ?? commission.mode,
                    ),
                    rateExam: toField(
                        commission.rateExam,
                        commission.modeExam ?? commission.mode,
                    ),
                    rateOrder: toField(
                        commission.rateOrder,
                        commission.modeOrder ?? commission.mode,
                    ),
                })
                setUpdatedBy(commission.updatedBy?.name ?? null)
                setUpdatedAt(commission.updatedAt ?? null)
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [userId])

    /** O critério que vale para um tipo: o próprio dele, ou o geral. */
    const modeOfType = (type: TypeKey): UserCommissionMode =>
        typeModes[type] || mode

    /** O critério do campo — a geral segue `mode`; cada tipo, o dele. */
    const modeOfKey = (key: RateKey): UserCommissionMode =>
        key === 'rate' ? mode : modeOfType(key.replace('rate', '') as TypeKey)

    /** Valor do campo no formato que o servidor guarda. */
    const valueOf = (key: RateKey): number | null => {
        const raw = rates[key]
        if (!raw.trim()) return null
        const parsed =
            modeOfKey(key) === 'PERCENTAGE'
                ? parsePercentToBasisPoints(raw)
                : parseCurrencyToCents(raw)
        return parsed > 0 ? parsed : null
    }

    const setRate = (key: RateKey, value: string) => {
        // Em valor fixo o campo é moeda mascarada; em percentual, número solto.
        const sanitized =
            modeOfKey(key) === 'PERCENTAGE'
                ? value.replace(/[^\d.,]/g, '')
                : currencyFormat(value, true)
        setRates(prev => ({ ...prev, [key]: sanitized }))
    }

    const switchMode = (next: UserCommissionMode) => {
        setMode(next)
        // A taxa não sobrevive à troca de critério: "10" é 10% ali e R$ 0,10 aqui.
        // Só as dos tipos que herdavam o geral são limpas — as de critério próprio
        // continuam válidas.
        setRates(prev => ({
            rate: '',
            rateConsultation: typeModes.Consultation ? prev.rateConsultation : '',
            rateExam: typeModes.Exam ? prev.rateExam : '',
            rateOrder: typeModes.Order ? prev.rateOrder : '',
        }))
    }

    /** Troca o critério de UM tipo — a taxa dele é zerada pelo mesmo motivo. */
    const switchTypeMode = (type: TypeKey, next: TypeMode) => {
        setTypeModes(prev => ({ ...prev, [type]: next }))
        setRates(prev => ({ ...prev, [`rate${type}` as RateKey]: '' }))
    }

    const save = async () => {
        const payload = {
            mode,
            rate: valueOf('rate'),
            rateConsultation: valueOf('rateConsultation'),
            rateExam: valueOf('rateExam'),
            rateOrder: valueOf('rateOrder'),
            modeConsultation: typeModes.Consultation || null,
            modeExam: typeModes.Exam || null,
            modeOrder: typeModes.Order || null,
        }

        const rateValues = [
            payload.rate,
            payload.rateConsultation,
            payload.rateExam,
            payload.rateOrder,
        ]
        if (!rateValues.some(value => value != null && value > 0)) {
            toast.error(
                'Informe ao menos uma taxa — geral ou de algum tipo de atendimento.'
            )
            return
        }

        // Critério próprio sem taxa própria não paga nada: a geral não é aproveitada
        // entre critérios diferentes. O backend recusa; avisar aqui poupa a viagem.
        const orphan = (['Consultation', 'Exam', 'Order'] as TypeKey[]).find(
            type =>
                typeModes[type] &&
                !((valueOf(`rate${type}` as RateKey) ?? 0) > 0)
        )
        if (orphan) {
            toast.error(
                'Tipo com critério próprio precisa da taxa dele — a geral não é aproveitada entre critérios diferentes.'
            )
            return
        }

        setSaving(true)
        try {
            const res = await upsertUserCommission(userId, payload)
            if (res.success) {
                toast.success('Comissão salva')
                setUpdatedAt(new Date().toISOString())
                setUpdatedBy(null)
            } else {
                toast.error(res.error || 'Não foi possível salvar a comissão')
            }
        } catch {
            toast.error('Erro de comunicação com o servidor')
        } finally {
            setSaving(false)
        }
    }

    const disable = async () => {
        setSaving(true)
        try {
            const res = await removeUserCommission(userId)
            if (res.success) {
                setEnabled(false)
                setRates({
                    rate: '',
                    rateConsultation: '',
                    rateExam: '',
                    rateOrder: '',
                })
                setTypeModes({ Consultation: '', Exam: '', Order: '' })
                toast.success(
                    'Comissão removida. Este usuário deixa de ser comissionável.'
                )
            } else {
                toast.error(res.error || 'Não foi possível remover a comissão')
            }
        } catch {
            toast.error('Erro de comunicação com o servidor')
        } finally {
            setSaving(false)
        }
    }

    // Sem permissão de RH nem de financeiro a seção nem aparece: quem administra
    // acesso não precisa saber quanto o colega recebe.
    if (!canEdit) return null

    const suffix = isPercentage ? '%' : ''
    const placeholder = isPercentage ? '0' : 'R$ 0,00'

    const typeFields: { type: TypeKey; key: RateKey; label: string }[] = [
        {
            type: 'Consultation',
            key: 'rateConsultation',
            label: labels.Consultations,
        },
        { type: 'Exam', key: 'rateExam', label: labels.Exams },
        { type: 'Order', key: 'rateOrder', label: labels.Orders },
    ]

    return (
        <div className="rounded-xl border border-base-300/70 p-4 flex flex-col gap-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-2">
                    <MdPaid className="text-xl text-secondary mt-0.5" />
                    <div>
                        <h3 className="font-semibold text-base-content">
                            Comissão por produtividade
                        </h3>
                        <p className="text-xs text-base-content/60 max-w-xl">
                            O que {userName || 'este usuário'} recebe pelos
                            atendimentos que agenda. É esta taxa que a aba
                            Produtividade dos relatórios aplica — lá ela não pode
                            mais ser digitada.
                        </p>
                    </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        className="toggle toggle-secondary toggle-sm"
                        checked={enabled}
                        disabled={loading || saving}
                        onChange={e => {
                            if (e.target.checked) setEnabled(true)
                            else if (updatedAt) disable()
                            else setEnabled(false)
                        }}
                    />
                    <span className="text-sm">Recebe comissão</span>
                </label>
            </div>

            {loading ? (
                <span className="loading loading-spinner loading-sm text-secondary" />
            ) : !enabled ? (
                <p className="text-xs text-base-content/50">
                    Sem comissão configurada. A apuração de produtividade deste
                    usuário fica bloqueada até uma taxa ser cadastrada aqui.
                </p>
            ) : (
                <>
                    <div role="tablist" className="tabs tabs-boxed w-fit">
                        <button
                            type="button"
                            role="tab"
                            className={`tab ${!isPercentage ? 'tab-active' : ''}`}
                            onClick={() => switchMode('FIXED')}
                        >
                            Valor fixo
                        </button>
                        <button
                            type="button"
                            role="tab"
                            className={`tab ${isPercentage ? 'tab-active' : ''}`}
                            onClick={() => switchMode('PERCENTAGE')}
                        >
                            Percentual
                        </button>
                    </div>

                    <div className="form-control w-full max-w-xs">
                        <label className="label py-1">
                            <span className="label-text">
                                {isPercentage
                                    ? 'Percentual sobre a receita'
                                    : 'Valor por atendimento'}
                            </span>
                        </label>
                        <label className="input input-bordered flex items-center gap-2">
                            <input
                                type="text"
                                className="grow"
                                inputMode={isPercentage ? 'decimal' : 'numeric'}
                                placeholder={placeholder}
                                value={rates.rate}
                                onChange={e => setRate('rate', e.target.value)}
                            />
                            {suffix && (
                                <span className="text-base-content/50">
                                    {suffix}
                                </span>
                            )}
                        </label>
                        <label className="label py-1">
                            <span className="label-text-alt text-base-content/50">
                                {isPercentage
                                    ? 'Sobre a receita de cada atendimento. Atendimento sem cobrança não entra na base.'
                                    : 'Por atendimento agendado, tenha ele gerado cobrança ou não.'}
                            </span>
                        </label>
                    </div>

                    <div className="flex flex-col gap-2">
                        <span className="text-sm font-medium text-base-content">
                            Por tipo de atendimento{' '}
                            <span className="text-base-content/40 text-xs font-normal">
                                (opcional — em branco usa o geral)
                            </span>
                        </span>
                        {/* Cada tipo pode ter CRITÉRIO próprio, e não só taxa própria:
                            "R$ 5,00 por consulta agendada e 10% sobre exames" é um
                            acordo real. Escolhido um critério diferente do geral, a
                            taxa daquele tipo passa a ser obrigatória — a geral não é
                            aproveitada entre critérios diferentes. */}
                        <div className="flex flex-col gap-2">
                            {typeFields.map(field => {
                                const typeMode = modeOfType(field.type)
                                const own = !!typeModes[field.type]
                                const typeIsPercentage = typeMode === 'PERCENTAGE'
                                return (
                                    <div
                                        key={field.key}
                                        className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] items-center gap-2"
                                    >
                                        <span className="text-sm text-base-content">
                                            {field.label}
                                        </span>
                                        <select
                                            className="select select-bordered select-sm w-44"
                                            value={typeModes[field.type]}
                                            onChange={e =>
                                                switchTypeMode(
                                                    field.type,
                                                    e.target.value as TypeMode
                                                )
                                            }
                                        >
                                            <option value="">
                                                Igual ao geral (
                                                {isPercentage
                                                    ? 'percentual'
                                                    : 'valor fixo'}
                                                )
                                            </option>
                                            <option value="FIXED">Valor fixo</option>
                                            <option value="PERCENTAGE">
                                                Percentual
                                            </option>
                                        </select>
                                        <label className="input input-bordered input-sm flex items-center gap-2 w-40">
                                            <input
                                                type="text"
                                                className="grow"
                                                inputMode={
                                                    typeIsPercentage
                                                        ? 'decimal'
                                                        : 'numeric'
                                                }
                                                placeholder={
                                                    typeIsPercentage ? '0' : 'R$ 0,00'
                                                }
                                                value={rates[field.key]}
                                                onChange={e =>
                                                    setRate(field.key, e.target.value)
                                                }
                                                // Critério próprio exige a taxa dele:
                                                // marcar o campo evita o save falhar
                                                // sem o operador saber onde.
                                                required={own}
                                            />
                                            {typeIsPercentage && (
                                                <span className="text-base-content/50">
                                                    %
                                                </span>
                                            )}
                                        </label>
                                    </div>
                                )
                            })}
                        </div>
                        <span className="text-xs text-base-content/50">
                            Tipo sem taxa e sem critério próprio usa a taxa geral. Tipo
                            com critério próprio precisa da taxa dele.
                        </span>
                    </div>

                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <span className="text-xs text-base-content/50 flex items-center gap-1">
                            <MdInfoOutline />
                            {updatedAt
                                ? `Atualizada em ${new Date(updatedAt).toLocaleDateString('pt-BR')}${
                                      updatedBy ? ` por ${updatedBy}` : ''
                                  }`
                                : 'Ainda não salva'}
                        </span>
                        {/* Botão próprio: a comissão é gravada por um endpoint com
                            permissão diferente da do cadastro do usuário, e salvar
                            junto faria o formulário inteiro falhar por causa dela. */}
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={saving}
                            onClick={save}
                        >
                            {saving ? (
                                <span className="loading loading-spinner loading-xs" />
                            ) : (
                                'Salvar comissão'
                            )}
                        </button>
                    </div>
                </>
            )}
        </div>
    )
}
